/**
 * Preferred media devices, stored and resolved by label
 *
 * The preferred microphone, speaker, camera and ringer used to be stored as the browser's deviceId.
 * That id is a per-origin salted hash: it rotates when site data is cleared or the profile changes,
 * and it means nothing on another machine. A stored id therefore stopped resolving, the resolver
 * fell through to 'default', and the app started up on "Standard enhed" although a device had been
 * chosen. The raw device label survives both, so that is what is stored now.
 *
 * Resolution walks the stored labels in order and takes the first device that carries one, first by
 * exact label, then tolerant of the Windows enumeration ordinal ("2- ") that moves with the USB port.
 * When nothing stored is present the microphone and speaker fall back to any headset, so a headset
 * beats the pc's own microphone and speakers on a machine that has not been set up yet.
 *
 * Stored lists may still hold deviceIds written by older versions; those are migrated to labels on
 * startup, or dropped when the device they named is no longer there to look up.
 *
 * The camera list can instead hold the single entry NO_CAMERA: the user chose "Benyt ikke". That is
 * not the same as an empty list, which means no camera was ever chosen - the first camera is used then.
 */

import { MediaDeviceType, type MediaDevice } from '@/stores/MediaDevices.types'

/** How many devices each preferred list remembers */
export const PREFERRED_DEVICES_MAX = 10

/** The preferred camera list's only entry when the user chose "Benyt ikke" - never a device label */
export const NO_CAMERA = 'NONE'

/** Matches a headset label - preferred over the pc's own microphone/speakers when nothing stored is present */
export const HEADSET_LABEL_PATTERN = /yealink|jabra|sennheiser|epos/i

/** Matches raw labels of known virtual audio/video devices that must never become a preferred device */
const VIRTUAL_DEVICE_PATTERN = /virtual|virtuel|vb-audio|vb-cable|voicemeeter|steinberg|blackhole|soundflower|aggregate|multi-output|loopback|cable/i

/** A Chrome deviceId: 64 lowercase hex characters */
const DEVICE_ID_PATTERN = /^[0-9a-f]{64}$/

/** Pseudo ids the browser uses for the system default and communications devices */
const PSEUDO_DEVICE_IDS = ['default', 'communications']

/**
 * True for a value written by an older version that stored deviceIds rather than labels
 * @param value Entry from a preferred list
 * @returns True if it is a deviceId or a pseudo id, false if it is a label
 */
export function isLegacyDeviceId(value: string) {
	return PSEUDO_DEVICE_IDS.includes(value) || DEVICE_ID_PATTERN.test(value)
}

/**
 * Reduce a raw label to the part that stays the same when the device moves to another USB port:
 * Windows prefixes an enumeration ordinal ("2- ") that changes with the port, and Chrome appends the
 * usb product id. A "-2" model suffix is not an ordinal and is kept, so two identical monitors stay apart.
 * @param rawLabel Raw label as enumerated by the browser
 * @returns The label without ordinal and product id
 */
export function comparableDeviceLabel(rawLabel: string) {
	return rawLabel
		.replace(/\b\d+- (?=\S)/g, '')
		.replace(/ \([0-9a-fA-F]{4}:[0-9a-fA-F]{4}\)$/, '')
		.replace(/ {2,}/g, ' ')
		.trim()
}

/**
 * True if a device must never be stored as a preferred device: the system default or communications
 * pseudo device, which means something different on every machine, or a virtual device
 * @param device Device to check
 * @returns True if the device is excluded
 */
export function isExcludedDevice(device: MediaDevice) {
	if (PSEUDO_DEVICE_IDS.includes(device.deviceId)) { return true }
	return VIRTUAL_DEVICE_PATTERN.test(device.rawLabel)
}

/**
 * Put a label at the front of a preferred list, keeping each label once and the list within its cap
 * @param list Current preferred labels, most recently chosen first
 * @param label Label the user just chose
 * @returns The new list
 */
export function pushPreferredLabel(list: readonly string[], label: string) {
	if (!label) { return [...list] }
	return [label, ...list.filter(item => item !== label)].slice(0, PREFERRED_DEVICES_MAX)
}

/**
 * True when the user chose not to use a camera ("Benyt ikke")
 * @param list Preferred camera labels
 * @returns True for "Benyt ikke", false for a chosen camera and for a camera never chosen
 */
export function isNoCameraChosen(list: readonly string[]) {
	return list[0] === NO_CAMERA
}

export interface ResolvePreferredDeviceOptions {
	/** Fall back to any headset when nothing stored is present - for the microphone and speaker, not the ringer */
	headsetFallback: boolean
	/** Fall back to the first device of the kind when nothing stored is present - for the camera */
	firstDeviceFallback?: boolean
}

/**
 * Find the device to use for a kind from the stored preferred labels
 * @param preferred Preferred labels, most recently chosen first
 * @param devices Devices as enumerated right now
 * @param kind Kind of device wanted
 * @param options Resolution options
 * @returns The device to use, undefined when nothing stored is present and no fallback applies
 */
export function resolvePreferredDevice(preferred: readonly string[], devices: readonly MediaDevice[], kind: MediaDeviceType, options: ResolvePreferredDeviceOptions) {
	const candidates = devices.filter(device => device.kind === kind && device.rawLabel && !isExcludedDevice(device))

	for (const label of preferred) {
		if (!label || label === NO_CAMERA) { continue }

		const exact = candidates.find(device => device.rawLabel === label)
		if (exact) { return exact }

		const comparable = comparableDeviceLabel(label)
		const tolerant = candidates.find(device => comparableDeviceLabel(device.rawLabel) === comparable)
		if (tolerant) { return tolerant }
	}

	if (options.headsetFallback) {
		return candidates.find(device => HEADSET_LABEL_PATTERN.test(device.rawLabel))
	}
	if (options.firstDeviceFallback) {
		return candidates[0]
	}
	return undefined
}

/**
 * Bring a preferred list written by an older version up to date: deviceIds become the label of the
 * device they still resolve to and are dropped otherwise, labels are kept. While the devices carry no
 * labels (no permission yet) the list is left alone, as there is nothing safe to migrate to.
 * @param list Preferred list as loaded
 * @param devices Devices as enumerated right now
 * @param kind Kind of device the list is for
 * @returns The migrated list
 */
export function migratePreferredList(list: readonly string[], devices: readonly MediaDevice[], kind: MediaDeviceType) {
	const labeled = devices.filter(device => device.kind === kind && device.rawLabel)
	if (labeled.length === 0) { return [...list] }

	const migrated: string[] = []

	for (const entry of list) {
		const label = isLegacyDeviceId(entry) ? labeled.find(device => device.deviceId === entry && !isExcludedDevice(device))?.rawLabel : entry

		if (label && !migrated.includes(label)) { migrated.push(label) }
	}
	return migrated.slice(0, PREFERRED_DEVICES_MAX)
}
